import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { Fragment, type ReactElement } from 'react';
import { Icon } from '../display/Icon.tsx';
import styles from './Menu.module.css';

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
  readonly tone?: 'default' | 'danger' | undefined;
  readonly disabled?: boolean | undefined;
  readonly onSelect: () => void;
}

export interface MenuProps {
  /** The element that opens the menu (usually an IconButton). */
  readonly trigger: ReactElement;
  /** Groups of items; groups are separated by a divider, empty groups are skipped. */
  readonly sections: readonly (readonly MenuItem[])[];
  readonly align?: 'start' | 'end' | undefined;
}

export const Menu = ({ trigger, sections, align = 'end' }: MenuProps) => {
  const visible = sections.filter((section) => section.length > 0);
  return (
    <RadixMenu.Root modal={false}>
      <RadixMenu.Trigger asChild>{trigger}</RadixMenu.Trigger>
      <RadixMenu.Portal>
        <RadixMenu.Content className={styles.content} align={align} sideOffset={6} collisionPadding={8}>
          {visible.map((section, index) => (
            <Fragment key={section[0]?.id ?? index}>
              {index > 0 && <RadixMenu.Separator className={styles.separator} />}
              {section.map((item) => (
                <RadixMenu.Item
                  key={item.id}
                  className={clsx(styles.item, item.tone === 'danger' && styles.danger)}
                  disabled={item.disabled ?? false}
                  onSelect={item.onSelect}
                >
                  {item.icon && <Icon icon={item.icon} size="sm" />}
                  <span className={styles.label}>{item.label}</span>
                </RadixMenu.Item>
              ))}
            </Fragment>
          ))}
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
};
